<script setup lang="ts">
import type { PageType } from '~/types'
import HeroSection from '~/components/UI/HeroSection.vue'
import SafeHtml from '~/components/UI/SafeHtml.vue'

const props = withDefaults(defineProps<{
  title?: string,
  page?: PageType,
  type?: 'article' | 'blog' | 'homepage',
  // id stabile sul blocco di testo per il tracciamento esterno delle pagine
  // legali (Privacy Archive) — passato solo dalle pagine che ne hanno bisogno,
  // mai impostato di default per non duplicarlo su ogni pagina che usa <Page>
  contentId?: string,
  hero?: {
    classNames?: string,
    hasTitle?: boolean,
    hasSubtitle?: boolean,
    fitHeight?: boolean,
    paddingTop?: number,
    paddingBottom?: number
  }
}>(), { type: 'article' })

const { loading } = storeToRefs(useUIStore())

// WCAG 4.1.1 — "Empty heading": senza contenuto reale (sottotitolo CMS vuoto)
// l'<h2> viene comunque renderizzato vuoto se non esplicitamente disattivato
// pagina per pagina; qui si basa sull'effettiva presenza di un sottotitolo
const hasSubtitle = computed(() => props.hero?.hasSubtitle !== false && !!props.page?.subtitle)

const generateRandomWidth = () => {
  const minWidth = 75
  const maxWidth = 100
  const randomValue = Math.floor(Math.random() * (maxWidth - minWidth + 1)) + minWidth
  return `${randomValue}%`
}

watch(() => props.page, (value) => {
  if (value == undefined) return

  useMeta({
    title: value?.seo?.title || props.title,
    description: value?.seo?.metaDescription || undefined,
    keywords: value?.seo?.keywords || undefined,
    image: value?.seo?.socialAssetUrl || undefined,
  })
}, { deep: true, immediate: true })
</script>

<template>
  <section class="relative flex flex-col items-center">
    <div v-if="loading" class="w-full">
      <Skeleton
        class="rounded-xl"
        width="100%"
        :height="hero?.fitHeight ? 'calc(100vh - 112px)' : '360px'"
      />
      <div
        v-if="type === 'article'"
        class="relative max-w-prose w-full mx-auto flex flex-col justify-start items-start gap-4 py-8 rounded-[30px]"
      >
        <Skeleton
          v-for="idx in 20"
          :key="idx"
          class="rounded-xl"
          :width="generateRandomWidth()"
          height="16px"
        />
      </div>
      <div
        v-if="type === 'blog'"
        class="xl:container relative w-full flex flex-wrap justify-start items-start gap-8 px-4 py-8 xl:px-0"
      >
        <Skeleton
          v-for="idx in 6 "
          :key="idx"
          class="rounded-xl"
          width="240px"
          height="320px"
        />
      </div>
    </div>

    <template v-if="page">
      <HeroSection
        :asset-url="page.assetUrl"
        article
        :has-title="hero?.hasTitle"
        :has-subtitle="hasSubtitle"
        :fit-height="hero?.fitHeight"
        parallax
        :padding-top="hero?.paddingTop"
        :padding-bottom="hero?.paddingBottom"
      >
        <template #title>
          <slot name="hero-title">
            <SafeHtml v-if="page?.title" :raw-html="page?.title" tag="span" unwrap-paragraphs />
          </slot>
        </template>
        <template #subtitle>
          <slot name="hero-subtitle">
            <SafeHtml v-if="page?.subtitle" :raw-html="page?.subtitle" tag="span" unwrap-paragraphs />
          </slot>
        </template>
        <template #content>
          <slot name="hero-content" />
        </template>
      </HeroSection>

      <div
        class="relative w-full flex flex-col justify-center items-center rounded-[30px]"
        :class="{ ['px-8 gap-8 pb-16']: type === 'article' }"
      >
        <slot>
          <article
            v-if="page?.content"
            :id="contentId"
            class="relative max-w-prose w-full mx-auto flex flex-col gap-8 prose lg:prose pt-8"
          >
            <SafeHtml
              class="text-slate-900 font-light"
              :raw-html="page?.content"
              shift-headings
            />
            <!-- contenuto aggiuntivo che deve restare dentro il blocco tracciato
                 (contentId), es. la dichiarazione dei cookie in cookie-policy.vue -->
            <slot name="content-extra" />
          </article>
        </slot>
        <slot name="extra" />
      </div>
    </template>
  </section>
</template>
